import { supabase } from '../lib/supabase';
import { generateFeedbackForBooking, generateFeedbackURL, isFeedbackEnabled, deleteFeedbackForBooking } from '../lib/feedback';

// Define the structure of automation data fetched from database
interface AutomationData {
  id: string;
  business_id: string;
  title: string;
  sms_action_active: boolean;
  sms_action_message: string;
  email_action_active: boolean;
  email_action_subject: string;
  email_action_body: string;
  booking_status_trigger_statuses: string[];
  notify_person_active: boolean;
  notify_person_target: 'all_resources' | 'admin' | 'custom';
  notify_person_phone: string;
  notify_person_message: string;
  wait_action_active: boolean;
  wait_action_duration: number;
  wait_action_unit: string;
  sms_action_order?: number | null;
  email_action_order?: number | null;
  notify_person_action_order?: number | null;
  wait_action_order?: number | null;
}

interface BookingWithRelations {
  id: string;
  business_id: string;
  customer_id: string;
  status: string;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  drop_off_date: string | null;
  drop_off_time: string | null;
  booking_number: string;
  title?: string;
  services?: Array<{
    id?: string;
    name: string;
    vehicle_make?: string;
    vehicle_model?: string;
    vehicle_year?: number;
    vehicle_color?: string;
    service_id?: string;
    vehicle_id?: string;
  }>;
  businesses: {
    id: string;
    name: string;
  };
  customers: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  };
}

interface ProcessResult {
  success: boolean;
  results?: Array<{
    type: string;
    automation_id: string;
    success: boolean;
    error?: any;
  }>;
  error?: any;
}

// Helper function to format time in AM/PM format
function formatTimeAMPM(time: string): string {
  if (!time) return '';
  
  // Parse the time string (assuming format like "14:30:00")
  const [hours, minutes] = time.split(':').map(Number);
  
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

// Get active automations with the booking_status trigger enabled for a business
async function getActiveAutomationsForBusiness(businessId: string, newStatus: string): Promise<AutomationData[]> {
  console.log(`[STATUS TRIGGER] Looking for automations: business=${businessId}, status=${newStatus}`);
  
  if (!supabase) {
    console.error('[STATUS TRIGGER] Supabase client not initialized');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('automation_settings')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .eq('booking_status_active', true);

    if (error) {
      console.error('[STATUS TRIGGER] Error fetching automations:', error);
      return [];
    }
    
    console.log(`[STATUS TRIGGER] Found ${data?.length || 0} active automations with booking_status_active=true`);
    
    // Filter automations where the new status is in the trigger statuses array
    const matchingAutomations = (data || []).filter(automation => {
      const statusesMatch = Array.isArray(automation.booking_status_trigger_statuses) && 
             automation.booking_status_trigger_statuses.includes(newStatus);
      
      if (statusesMatch) {
        console.log(`[STATUS TRIGGER] ✅ Matched automation: ${automation.id} (${automation.title})`);
      }
      
      return statusesMatch;
    });
    
    console.log(`[STATUS TRIGGER] ${matchingAutomations.length} automations match status ${newStatus}`);
    
    return matchingAutomations;
  } catch (error) {
    console.error('[STATUS TRIGGER] Error fetching automations:', error);
    return [];
  }
}

// Process a booking status change by triggering appropriate automations
export async function processBookingStatusChange(bookingId: string, newStatus: string, previousStatus?: string): Promise<ProcessResult> {
  console.log(`[STATUS TRIGGER] 🔴 ===== BOOKING STATUS PROCESSOR CALLED =====`);
  console.log(`[STATUS TRIGGER] ⚡ Processing status change: booking=${bookingId}, ${previousStatus || 'unknown'} → ${newStatus}`);
  console.log(`[STATUS TRIGGER] 🔴 Should trigger bonus if new status is 'completed'`);
  
  if (!supabase) {
    console.error('[STATUS TRIGGER] Supabase client not initialized');
    return { success: false, error: 'Supabase client not initialized' };
  }

  try {
    // 🎯 PRIORITY 1: Process booking bonus FIRST based on status change
    if (newStatus === 'completed') {
      console.log(`[STATUS TRIGGER] 🔥 ===== STEP 1: CREATE BONUS (HIGH PRIORITY) =====`);
      console.log(`[STATUS TRIGGER] 🔥 Booking ID: ${bookingId}`);
      console.log(`[STATUS TRIGGER] 🔥 Status Change: ${previousStatus || 'unknown'} → ${newStatus}`);
      
      try {
        const { processBookingBonus, processServiceBonus } = await import('./bonusCalculationService');
        
        // Process booking bonus first
        const bonusResult = await processBookingBonus(bookingId);
        
        console.log(`[STATUS TRIGGER] 🔥 Booking Bonus Processing Result:`, bonusResult);
        
        if (bonusResult.success && bonusResult.bonusAmount > 0) {
          console.log(`[STATUS TRIGGER] ✅ 🔥 BOOKING BONUS CREATED SUCCESSFULLY!`);
          console.log(`[STATUS TRIGGER] ✅ 🔥 Amount: $${bonusResult.bonusAmount}`);
          console.log(`[STATUS TRIGGER] ✅ 🔥 Payroll Entry ID: ${bonusResult.payrollEntryId}`);
        } else if (bonusResult.error) {
          console.error(`[STATUS TRIGGER] ❌ 🔥 BOOKING BONUS PROCESSING FAILED: ${bonusResult.error}`);
        } else {
          console.log(`[STATUS TRIGGER] ℹ️ 🔥 No booking bonus applicable for this booking`);
          console.log(`[STATUS TRIGGER] ℹ️ 🔥 Reason: ${bonusResult.reason || 'No reason provided'}`);
        }
        
        console.log(`[STATUS TRIGGER] 🔥 ===== BOOKING BONUS CREATION COMPLETE =====`);
        
        // Now process service bonus
        console.log(`[STATUS TRIGGER] 🔥 ===== STEP 2: SERVICE BONUS PROCESSING =====`);
        const serviceBonusResult = await processServiceBonus(bookingId);
        
        console.log(`[STATUS TRIGGER] 🔥 Service Bonus Processing Result:`, serviceBonusResult);
        
        if (serviceBonusResult.success && serviceBonusResult.bonusAmount > 0) {
          console.log(`[STATUS TRIGGER] ✅ 🔥 SERVICE BONUS CREATED SUCCESSFULLY!`);
          console.log(`[STATUS TRIGGER] ✅ 🔥 Amount: $${serviceBonusResult.bonusAmount}`);
          console.log(`[STATUS TRIGGER] ✅ 🔥 Payroll Entry ID: ${serviceBonusResult.payrollEntryId}`);
        } else if (serviceBonusResult.error) {
          console.error(`[STATUS TRIGGER] ❌ 🔥 SERVICE BONUS PROCESSING FAILED: ${serviceBonusResult.error}`);
        } else {
          console.log(`[STATUS TRIGGER] ℹ️ 🔥 No service bonus applicable for this booking`);
          console.log(`[STATUS TRIGGER] ℹ️ 🔥 Reason: ${serviceBonusResult.reason || 'No reason provided'}`);
        }
        
        console.log(`[STATUS TRIGGER] 🔥 ===== SERVICE BONUS CREATION COMPLETE =====`);
        
      } catch (error) {
        console.error(`[STATUS TRIGGER] ❌ 🔥 UNEXPECTED ERROR in bonus processing:`, error);
        console.error(`[STATUS TRIGGER] ❌ 🔥 Error stack:`, error instanceof Error ? error.stack : 'No stack');
      }
    } else if (previousStatus === 'completed' && newStatus !== 'completed') {
      console.log(`[STATUS TRIGGER] 🗑️ ===== STEP 1: REMOVE BONUS (HIGH PRIORITY) =====`);
      console.log(`[STATUS TRIGGER] 🗑️ Booking ID: ${bookingId}`);
      console.log(`[STATUS TRIGGER] 🗑️ Status Change: ${previousStatus} → ${newStatus}`);
      console.log(`[STATUS TRIGGER] 🗑️ Removing bonus entries for booking moved away from completed status`);
      
      try {
        const { removeBookingBonus } = await import('./bonusCalculationService');
        const removeResult = await removeBookingBonus(bookingId);
        
        console.log(`[STATUS TRIGGER] 🗑️ Bonus Removal Result:`, removeResult);
        
        if (removeResult.success) {
          console.log(`[STATUS TRIGGER] ✅ 🗑️ BONUS REMOVED SUCCESSFULLY!`);
          console.log(`[STATUS TRIGGER] ✅ 🗑️ Entries Removed: ${removeResult.entriesRemoved}`);
        } else if (removeResult.error) {
          console.error(`[STATUS TRIGGER] ❌ 🗑️ BONUS REMOVAL FAILED: ${removeResult.error}`);
        } else {
          console.log(`[STATUS TRIGGER] ℹ️ 🗑️ No bonus entries found to remove`);
        }
        
        console.log(`[STATUS TRIGGER] 🗑️ ===== BONUS REMOVAL COMPLETE =====`);
      } catch (error) {
        console.error(`[STATUS TRIGGER] ❌ 🗑️ UNEXPECTED ERROR in bonus removal:`, error);
        console.error(`[STATUS TRIGGER] ❌ 🗑️ Error stack:`, error instanceof Error ? error.stack : 'No stack');
      }
    } else {
      console.log(`[STATUS TRIGGER] 🔥 NO BONUS ACTION: Status change "${previousStatus || 'unknown'}" → "${newStatus}" doesn't require bonus processing`);
    }

    // Handle feedback cleanup when moving from completed to another status
    if (previousStatus === 'completed' && newStatus !== 'completed') {
      console.log(`[STATUS TRIGGER] 🗑️ Booking moved from completed to ${newStatus}, cleaning up feedback`);
      try {
        const feedbackDeleted = await deleteFeedbackForBooking(bookingId);
        if (feedbackDeleted) {
          console.log(`[STATUS TRIGGER] ✅ Feedback cleanup successful for booking: ${bookingId}`);
        } else {
          console.warn(`[STATUS TRIGGER] ⚠️ Feedback cleanup failed for booking: ${bookingId}`);
        }
      } catch (error) {
        console.error(`[STATUS TRIGGER] ❌ Error during feedback cleanup:`, error);
      }
    }

    // Fetch detailed booking information
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .select(`
        id,
        customer_id,
        business_id,
        status,
        drop_off_date,
        drop_off_time,
        start_date,
        start_time,
        end_date,
        end_time,
        booking_number,
        title,
        businesses:business_id (
          id,
          name
        ),
        customers:customer_id (
          id,
          first_name,
          last_name,
          email,
          phone
        )
      `)
      .eq('id', bookingId)
      .single();

    if (bookingError || !booking) {
      console.error(`[STATUS TRIGGER] Failed to fetch booking: ${bookingError?.message || 'Booking not found'}`);
      return { success: false, error: bookingError || 'Booking not found' };
    }

    // Convert the nested data structure from arrays to single objects
    const typedBooking: BookingWithRelations = {
      ...booking,
      businesses: Array.isArray(booking.businesses) ? booking.businesses[0] : booking.businesses,
      customers: Array.isArray(booking.customers) ? booking.customers[0] : booking.customers
    };

    // Get active automations with booking_status trigger for this business and this specific status
    const automations = await getActiveAutomationsForBusiness(typedBooking.business_id, newStatus);
    
    if (automations.length === 0) {
      console.log(`[STATUS TRIGGER] No matching automations found for status ${newStatus}`);
      
      // Even if no automations, we still need to handle feedback generation for completed status
      if (newStatus === 'completed') {
        try {
          const feedbackIsEnabled = await isFeedbackEnabled(typedBooking.business_id);
          
          if (feedbackIsEnabled) {
            console.log(`[STATUS TRIGGER] 📝 Generating feedback link for completed booking (no automations): ${bookingId}`);
            const feedbackToken = await generateFeedbackForBooking(bookingId);
            
            if (feedbackToken) {
              const feedbackLink = generateFeedbackURL(feedbackToken);
              console.log(`[STATUS TRIGGER] ✅ Feedback link generated: ${feedbackLink}`);
              
              // Store the feedback link in the bookings table
              try {
                const { error: updateError } = await supabase
                  .from('bookings')
                  .update({ feedback_link: feedbackLink })
                  .eq('id', bookingId);
                
                if (updateError) {
                  console.error(`[STATUS TRIGGER] ❌ Failed to store feedback link in bookings table:`, updateError);
                } else {
                  console.log(`[STATUS TRIGGER] ✅ Feedback link stored in bookings table successfully`);
                }
              } catch (error) {
                console.error(`[STATUS TRIGGER] ❌ Error storing feedback link in bookings table:`, error);
              }
            } else {
              console.warn(`[STATUS TRIGGER] ⚠️ Failed to generate feedback link for booking: ${bookingId}`);
            }
          }
        } catch (error) {
          console.error(`[STATUS TRIGGER] ❌ Error generating feedback link:`, error);
        }
      }
      
      return { success: true, results: [] };
    }

    // Get vehicle information from the first service if available
    let vehicle_make = '';
    let vehicle_model = '';
    let vehicle_year = '';
    let vehicle_color = '';
    let service_name = 'Appointment'; // Default value
    
    if (typedBooking.services && typedBooking.services.length > 0) {
      const firstService = typedBooking.services[0];
      vehicle_make = firstService.vehicle_make || '';
      vehicle_model = firstService.vehicle_model || '';
      vehicle_year = firstService.vehicle_year ? firstService.vehicle_year.toString() : '';
      vehicle_color = firstService.vehicle_color || '';
      service_name = firstService.name || 'Appointment';
      
      console.log('[STATUS TRIGGER] Vehicle information:', {
        vehicle: `${vehicle_year} ${vehicle_make} ${vehicle_model} (${vehicle_color})`.trim(),
        service: service_name
      });
    }

    // Prepare template data for SMS and email messages
    const templateData: Record<string, string> = {
      customer_first_name: typedBooking.customers.first_name || '',
      customer_last_name: typedBooking.customers.last_name || '',
      customer_full_name: `${typedBooking.customers.first_name || ''} ${typedBooking.customers.last_name || ''}`.trim(),
      drop_off_date_formatted: typedBooking.drop_off_date 
        ? new Date(typedBooking.drop_off_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      drop_off_time_formatted: formatTimeAMPM(typedBooking.drop_off_time || ''),
      start_date_formatted: typedBooking.start_date 
        ? new Date(typedBooking.start_date).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
          })
        : '',
      start_time_formatted: formatTimeAMPM(typedBooking.start_time || ''),
      business_name: typedBooking.businesses.name || '',
      appointment_id: typedBooking.id,
      booking_id: typedBooking.id,
      booking_number: typedBooking.booking_number || typedBooking.id.split('-')[0] || typedBooking.id,
      booking_title: typedBooking.title || 'Untitled Booking',
      service_name,
      vehicle_make,
      vehicle_model,
      vehicle_year,
      vehicle_color,
      vehicle: vehicle_make && vehicle_model ? 
        `${vehicle_year ? vehicle_year + ' ' : ''}${vehicle_make} ${vehicle_model}${vehicle_color ? ' (' + vehicle_color + ')' : ''}` : 
        'No vehicle information',
      phoneNumber: typedBooking.customers.phone || '',
      email: typedBooking.customers.email || '',
      drop_off_date: typedBooking.drop_off_date || '',
      drop_off_time: typedBooking.drop_off_time || '',
      status: newStatus,
      status_formatted: newStatus === 'in_progress' ? 'In Progress' : 
                       newStatus === 'completed' ? 'Completed' : 
                       newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
      status_date: new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
      }),
      status_time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'UTC'
      })
    };

    // Generate feedback link for completed bookings BEFORE processing automations
    let feedbackLink = '';
    if (newStatus === 'completed') {
      try {
        // Check if feedback is enabled for this business
        const feedbackIsEnabled = await isFeedbackEnabled(typedBooking.business_id);
        
        if (feedbackIsEnabled) {
          console.log(`[STATUS TRIGGER] 📝 Generating feedback link for completed booking: ${bookingId}`);
          console.log(`[STATUS TRIGGER] 🔍 Business ID: ${typedBooking.business_id}`);
          console.log(`[STATUS TRIGGER] 🔍 Customer info:`, {
            name: `${typedBooking.customers.first_name} ${typedBooking.customers.last_name}`,
            email: typedBooking.customers.email,
            phone: typedBooking.customers.phone
          });
          
          const feedbackToken = await generateFeedbackForBooking(bookingId);
          
          if (feedbackToken) {
            feedbackLink = generateFeedbackURL(feedbackToken);
            console.log(`[STATUS TRIGGER] ✅ Feedback link generated successfully: ${feedbackLink}`);
            console.log(`[STATUS TRIGGER] 🔍 Feedback token: ${feedbackToken}`);
            console.log(`[STATUS TRIGGER] 🔍 Feedback link length: ${feedbackLink.length}`);
            
            // Store the feedback link in the bookings table
            try {
              const { error: updateError } = await supabase
                .from('bookings')
                .update({ feedback_link: feedbackLink })
                .eq('id', bookingId);
              
              if (updateError) {
                console.error(`[STATUS TRIGGER] ❌ Failed to store feedback link in bookings table:`, updateError);
              } else {
                console.log(`[STATUS TRIGGER] ✅ Feedback link stored in bookings table successfully`);
              }
            } catch (error) {
              console.error(`[STATUS TRIGGER] ❌ Error storing feedback link in bookings table:`, error);
            }
          } else {
            console.error(`[STATUS TRIGGER] ❌ Failed to generate feedback token for booking: ${bookingId}`);
            console.log(`[STATUS TRIGGER] 🔍 generateFeedbackForBooking returned:`, feedbackToken);
          }
        } else {
          console.log(`[STATUS TRIGGER] 🔒 Feedback disabled for business ${typedBooking.business_id}, skipping feedback generation`);
        }
      } catch (error) {
        console.error(`[STATUS TRIGGER] ❌ Error generating feedback link:`, error);
        console.error(`[STATUS TRIGGER] ❌ Error details:`, {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : 'No stack trace'
        });
      }
    }

    // TEMPORARY TEST: Force a test URL to see if variable replacement works
    if (newStatus === 'completed' && !feedbackLink) {
      feedbackLink = 'https://test-feedback-url.com/test-token-123';
      console.log(`[STATUS TRIGGER] 🧪 USING TEST FEEDBACK URL: ${feedbackLink}`);
    }

    // Add feedback link to template data
    templateData.feedback_link = feedbackLink;
    templateData.feedback_url = feedbackLink; // Also add as feedback_url for backward compatibility
    
    console.log(`[STATUS TRIGGER] 🔗 Template data feedback_link: ${feedbackLink ? 'Generated' : 'Empty'}`);
    console.log(`[STATUS TRIGGER] 🔗 Final feedback_link value: "${feedbackLink}"`);
    console.log(`[STATUS TRIGGER] 🔗 Final feedback_url value: "${templateData.feedback_url}"`);
    console.log(`[STATUS TRIGGER] 🔗 All template data keys:`, Object.keys(templateData));
    console.log(`[STATUS TRIGGER] 🔗 Complete template data object:`, templateData);



    // IMPORTANT: Add delay for completed bookings to ensure feedback token is fully committed
    // This prevents race condition where SMS sends before feedback link is available
    if (newStatus === 'completed' && feedbackLink) {
      console.log(`[STATUS TRIGGER] ⏱️ Adding 5-second delay to ensure feedback token is committed to database...`);
      await new Promise(resolve => setTimeout(resolve, 5000));
      console.log(`[STATUS TRIGGER] ✅ Delay completed, proceeding with automation processing`);
    }

    // Import the actions for direct execution
    const { SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION, WAIT_ACTION } = await import('../lib/automations/actions');
    
    const results = [];

    // Process each automation
    for (const automation of automations) {
      console.log(`[STATUS TRIGGER] Processing automation: ${automation.id} (${automation.title})`);
      
      // Collect all active actions with their order information
      const actionsToExecute: Array<{ action: any, order: number, type: string }> = [];
      
      // Check for SMS action
      if (automation.sms_action_active && typedBooking.customers.phone) {
        actionsToExecute.push({
          action: {
            ...SMS_ACTION,
            settings: {
              ...SMS_ACTION.settings,
              message: automation.sms_action_message || ''
            }
          },
          order: automation.sms_action_order || 999,
          type: 'sms'
        });
      }
      
      // Check for Email action
      if (automation.email_action_active && typedBooking.customers.email) {
        actionsToExecute.push({
          action: {
            ...EMAIL_ACTION,
            settings: {
              ...EMAIL_ACTION.settings,
              subject: automation.email_action_subject || '',
              emailBody: automation.email_action_body || ''
            }
          },
          order: automation.email_action_order || 999,
          type: 'email'
        });
      }
      
      // Check for Notify Person action
      if (automation.notify_person_active) {
        actionsToExecute.push({
          action: {
            ...NOTIFY_PERSON_ACTION,
            settings: {
              ...NOTIFY_PERSON_ACTION.settings,
              notifyTarget: automation.notify_person_target || 'admin',
              notifyPhoneNumber: automation.notify_person_phone || '',
              notifyMessage: automation.notify_person_message || ''
            }
          },
          order: automation.notify_person_action_order || 999,
          type: 'notify_person'
        });
      }
      
      // Check for Wait action
      if (automation.wait_action_active) {
        actionsToExecute.push({
          action: {
            ...WAIT_ACTION,
            settings: {
              ...WAIT_ACTION.settings,
              waitDuration: automation.wait_action_duration || 30,
              waitUnit: (automation.wait_action_unit as 'minutes' | 'hours' | 'days') || 'minutes'
            }
          },
          order: automation.wait_action_order || 999,
          type: 'wait'
        });
      }
      
      // Sort actions by their order and execute them in sequence
      const sortedActions = actionsToExecute.sort((a, b) => a.order - b.order);
      
      console.log(`[STATUS TRIGGER] Executing ${sortedActions.length} actions in order:`, 
        sortedActions.map(a => `${a.type} (order: ${a.order})`).join(' → '));
      
      // Execute actions in the correct order
      for (const { action, type } of sortedActions) {
        try {
          console.log(`[STATUS TRIGGER] 🔄 Executing ${type} action...`);
          
          if (type === 'wait') {
            console.log(`[STATUS TRIGGER] ⏱️ STARTING WAIT: ${action.settings.waitDuration} ${action.settings.waitUnit}`);
            
            // Execute the wait action and WAIT for it to complete
            await action.execute(typedBooking.business_id, templateData);
            
            console.log(`[STATUS TRIGGER] ✅ WAIT COMPLETED - continuing to next action`);
            results.push({ type: 'wait', automation_id: automation.id, success: true });
          } else if (type === 'sms') {
          console.log(`[STATUS TRIGGER] 📱 Sending SMS to customer: ${typedBooking.customers.phone}`);
            console.log(`[STATUS TRIGGER] 📱 SMS message template: "${action.settings.message}"`);
          console.log(`[STATUS TRIGGER] 📱 Template data being passed:`, {
            feedback_link: templateData.feedback_link,
            feedback_url: templateData.feedback_url,
            customer_first_name: templateData.customer_first_name,
            business_name: templateData.business_name,
            phoneNumber: templateData.phoneNumber,
            totalKeys: Object.keys(templateData).length
          });
          
          // Ensure the phone number is available in the format the SMS action expects
          const smsTemplateData: Record<string, string> = {
            ...templateData,
              phoneNumber: typedBooking.customers.phone || ''
          };
          
          console.log(`[STATUS TRIGGER] 📱 Final SMS template data:`, {
            hasPhoneNumber: !!smsTemplateData.phoneNumber,
            phoneNumber: smsTemplateData.phoneNumber,
            hasFeedbackLink: !!smsTemplateData.feedback_link,
            feedback_link: smsTemplateData.feedback_link
          });
          
          // Execute the SMS action
            await action.execute(typedBooking.business_id, smsTemplateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: typedBooking.business_id,
            automation_id: automation.id,
            action_type: 'sms',
            recipient: typedBooking.customers.phone,
            trigger_type: 'booking_status',
            trigger_data: {
              booking_id: typedBooking.id,
              status: newStatus,
              timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'sms', automation_id: automation.id, success: true });
          } else if (type === 'email') {
          console.log(`[STATUS TRIGGER] 📧 Sending email to customer: ${typedBooking.customers.email}`);
          
          // Execute the Email action
            await action.execute(typedBooking.business_id, templateData);
          
          // Log the action
          await supabase.from('automation_action_log').insert({
            business_id: typedBooking.business_id,
            automation_id: automation.id,
            action_type: 'email',
            recipient: typedBooking.customers.email,
            trigger_type: 'booking_status',
            trigger_data: {
              booking_id: typedBooking.id,
              status: newStatus,
              timestamp: new Date().toISOString(),
              vehicle_make,
              vehicle_model,
              vehicle_year,
              vehicle_color,
              service_name
            }
          });
          
          results.push({ type: 'email', automation_id: automation.id, success: true });
          } else if (type === 'notify_person') {
            console.log(`[STATUS TRIGGER] 🔔 Sending notification to ${action.settings.notifyTarget}`);
          
          // Execute the notify person action
            await action.execute(typedBooking.business_id, templateData);
            
            results.push({ type: 'notify_person', automation_id: automation.id, success: true });
          }
          
          console.log(`[STATUS TRIGGER] ✅ ${type} action completed successfully`);
        } catch (error) {
          console.error(`[STATUS TRIGGER] ❌ ${type} action failed:`, error);
          results.push({ type, automation_id: automation.id, success: false, error });
        }
      }
    }
    
    console.log(`[STATUS TRIGGER] ✅ Successfully processed ${results.length} actions for status ${newStatus}`);
    
    return { success: true, results };
  } catch (error) {
    console.error(`[STATUS TRIGGER] ❌ Failed to process status change:`, error);
    return { success: false, error };
  }
} 